/** Retention-policy editor — Phase 2B-ui.
 *
 * Org admins set `retention_days` on their org; the nightly Celery
 * sweep deletes Sessions older than this many days, skipping anything
 * with an Evidence row on legal_hold. NULL = keep forever.
 *
 * Wraps PATCH /v1/orgs/me/retention (gated on `retention.update`).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiPaths, api } from "@/lib/api";
import type { Organization } from "@/lib/types";


export function RetentionPolicyCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const org = useQuery({
    queryKey: ["org-me"],
    queryFn: async () => (await api.get<Organization>(ApiPaths.orgMe)).data,
  });
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string>("");

  useEffect(() => {
    if (org.data && !editing) {
      setDraft(
        org.data.retention_days === null ? "" : String(org.data.retention_days),
      );
    }
  }, [org.data, editing]);

  const save = useMutation({
    mutationFn: async (payload: { retention_days: number | null }) => {
      const res = await api.patch<Organization>(ApiPaths.orgRetention, payload);
      return res.data;
    },
    onSuccess: (next) => {
      qc.setQueryData(["org-me"], next);
      setEditing(false);
    },
  });

  if (org.isLoading)
    return (
      <section className="card text-sm text-nb-text-2">
        {t("admin.retention.loading")}
      </section>
    );
  if (!org.data) return null;

  const onSave = () => {
    const trimmed = draft.trim();
    if (trimmed === "") {
      save.mutate({ retention_days: null });
      return;
    }
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed) || parsed < 1 || !Number.isInteger(parsed)) {
      return;
    }
    save.mutate({ retention_days: parsed });
  };

  const current = org.data.retention_days;

  return (
    <section className="card space-y-3">
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
            {t("admin.retention.cardTitle")}
          </h2>
          <p className="text-xs text-nb-text-3">{t("admin.retention.cardHint")}</p>
        </div>
      </header>
      <div className="flex flex-wrap items-baseline gap-3">
        {!editing ? (
          <>
            <p className="text-sm text-nb-text">
              <span className="text-nb-text-2">{t("admin.retention.currentLabel")}</span>{" "}
              {current === null ? (
                <span className="font-medium text-cvp-warn">
                  {t("admin.retention.noAutoDeletion")}
                </span>
              ) : (
                <span className="font-medium">
                  {t("admin.retention.daysSuffix", { count: current })}
                </span>
              )}
            </p>
            <button
              type="button"
              className="btn-ghost text-sm"
              onClick={() => setEditing(true)}
            >
              {t("admin.retention.editButton")}
            </button>
          </>
        ) : (
          <>
            <label className="flex items-baseline gap-2 text-sm">
              <span className="text-nb-text-2">{t("admin.retention.daysLabel")}</span>
              <input
                className="input w-32"
                type="number"
                min={1}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={t("admin.retention.daysPlaceholder")}
                aria-label={t("admin.retention.daysAriaLabel")}
              />
            </label>
            <button
              type="button"
              className="btn-primary text-sm"
              onClick={onSave}
              disabled={save.isPending}
            >
              {save.isPending
                ? t("admin.retention.saving")
                : t("admin.retention.saveButton")}
            </button>
            <button
              type="button"
              className="btn-ghost text-sm"
              onClick={() => {
                setEditing(false);
                setDraft(
                  current === null ? "" : String(current),
                );
              }}
            >
              {t("admin.retention.cancelButton")}
            </button>
          </>
        )}
      </div>
      {save.isError && (
        <p className="text-xs text-cvp-danger">
          {(save.error as Error).message ?? t("admin.retention.saveErrorGeneric")}
        </p>
      )}
    </section>
  );
}
